/**
 * IncassoListCells
 *
 * Cell rendering factory for the IncassoListPage table.
 * Adapts the patterns from the original incassoCellFactory for IncassoTicket data.
 */

import { Spinner, Tooltip } from '@heroui/react';
import {
  ArrowSquareOut,
  BellSimple,
  Buildings,
  ClipboardText,
  Envelope,
  Invoice,
  ListChecks,
  PencilSimple,
  Scales,
  UsersThree,
} from '@phosphor-icons/react';
import { ArrowUpRight } from 'assets/icons';
import ButtonNew from 'components/buttonNew/buttonNew';
import { Link } from 'react-router-dom';
import { TwilioCallButton } from 'shared/components/TwilioCallButton/TwilioCallButton';
import { DevWhatsAppButton } from 'shared/components/TwilioCallButton/DevWhatsAppButton';
import { applyMask } from 'shared/utils/masks';
import {
  LegalProcedureSummary,
  LegalProcedureStatus,
  LEGAL_PROCEDURE_TYPE_LABELS,
  PROCEDURE_DATE_TYPE_LABELS,
} from 'shared/types/legal-procedure';
import { PaymentPlanSummary, PaymentPlanStatus } from 'shared/types/payment-plan';
import type { CellConfig } from 'shared/components/UniversalTable/components/UniversalTableRow';
import { BeoordelingPopover } from '../components/BeoordelingPopover';
import { PhasePickerPopover } from '../components/PhasePickerPopover';
import { SnoozeIndicator } from '../components/SnoozeIndicator';
import { IncassoTicket, IncassoBeoordeling, IncassoPhase } from '../shared/incassoTypes';
import type { TableColumnsConfig } from 'shared/components/UniversalTable/types';

// ─── Column Configuration ────────────────────────────────────────────────────

export const INCASSO_LIST_COLUMNS: TableColumnsConfig = {
  columns: [
    { id: 'phase', label: 'Fase', minWidth: 100, defaultWeight: 1.2 },
    { id: 'beoordeling', label: 'Beoordeling', minWidth: 40, defaultWeight: 0.4 },
    { id: 'emails', label: 'Mails', minWidth: 60, defaultWeight: 0.6, className: 'flex justify-center' },
    { id: 'amount', label: 'Bedrag', minWidth: 80, defaultWeight: 1, sortKey: 'cachedTotalDebt' },
    { id: 'companyName', label: 'Vennootschap', minWidth: 100, defaultWeight: 1.2 },
    { id: 'unit', label: 'Unit', minWidth: 80, defaultWeight: 0.8 },
    { id: 'customer', label: 'Klant', minWidth: 150, defaultWeight: 2 },
    { id: 'notes', label: 'Notities', minWidth: 120, defaultWeight: 1.5 },
    { id: 'invoices', label: 'Facturen', minWidth: 80, defaultWeight: 0.8 },
    { id: 'daysOverdue', label: 'Te laat', minWidth: 60, defaultWeight: 0.6, sortKey: 'cachedMaxDaysOverdue' },
    { id: 'snooze', label: 'Snooze', minWidth: 90, defaultWeight: 0.8 },
    { id: 'actions', label: 'Acties', minWidth: 200, defaultWeight: 2 },
  ],
  localStorageKey: 'incasso-list-columns-v7',
};

// ─── Cell Context ────────────────────────────────────────────────────────────

export interface IncassoListCellContext {
  generatingLetters: Record<number, boolean>;
  generatedLetterUrls: Record<number, string>;
  legalSummaries: Record<number, LegalProcedureSummary[]>;
  paymentPlanSummaries: Record<number, PaymentPlanSummary[]>;
  emailCounts: Record<number, { total: number; unread: number }>;
  onGenerateLetter: (ticket: IncassoTicket) => void;
  onEditNotes: (ticket: IncassoTicket) => void;
  onOpenDetailSheet: (ticket: IncassoTicket) => void;
  onViewIncassoActions: (ticket: IncassoTicket) => void;
  onViewLegalProcedures: (ticket: IncassoTicket) => void;
  onViewPaymentPlans: (ticket: IncassoTicket) => void;
  onLogPhoneCall: (ticket: IncassoTicket, phoneNumber: string, durationSeconds?: number) => void;
  onBeoordelingChange: (ticket: IncassoTicket, beoordeling: IncassoBeoordeling | null, reden?: string) => void;
  onPhaseChange: (ticket: IncassoTicket, newPhase: IncassoPhase) => void;
  onSnooze: (ticket: IncassoTicket) => void;
}

// ─── Cell Factory ────────────────────────────────────────────────────────────

export const createIncassoListCells = (
  ticket: IncassoTicket,
  ctx: IncassoListCellContext,
): CellConfig[] => {
  const totalDebt = Number(ticket.cachedTotalDebt) || 0;
  const maxDays = ticket.cachedMaxDaysOverdue ?? 0;
  const phoneNumber = ticket.contact?.phone_mobile || ticket.contact?.phone_direct || ticket.company?.phone_direct;
  const notes = ticket.contact?.notes || ticket.company?.notes || null;
  const partnerId = ticket.odooPartnerId;
  const ticketId = Number(ticket.id);

  // Legal procedure summaries from fetched data
  const legalProcs: LegalProcedureSummary[] = ctx.legalSummaries[partnerId] || [];
  const activeLegalProcs = legalProcs.filter(
    (p) => p.status === LegalProcedureStatus.ACTIVE || p.status === LegalProcedureStatus.PENDING,
  );
  const legalProcedureCount = legalProcs.length;

  // Payment plan summaries from fetched data
  const planSummaries: PaymentPlanSummary[] = ctx.paymentPlanSummaries[partnerId] || [];
  const activePlans = planSummaries.filter((p) => p.status === PaymentPlanStatus.ACTIVE);
  const paymentPlanCount = planSummaries.length;

  // Action count from enrichment
  const actionCount = ticket.incassoActionCount || 0;

  // Email indicator: total = sent + received, unread = received & unread
  const emailStats = ctx.emailCounts[partnerId] || { total: 0, unread: 0 };
  const emailCount = emailStats.total;
  const emailUnreadCount = emailStats.unread;

  // Snooze state
  const isSnoozed = ticket.snoozeUntil ? new Date(ticket.snoozeUntil) > new Date() : false;

  // Row coloring: legal > payment plan > snoozed (only one applies)
  let rowClassName: string | undefined;
  if (activeLegalProcs.length > 0) {
    rowClassName = 'bg-red-50/50';
  } else if (activePlans.length > 0) {
    rowClassName = 'bg-blue-50/50';
  } else if (isSnoozed) {
    rowClassName = 'bg-neutral-50 opacity-60';
  }

  const renderPhoneActions = (phone: string) => (
    <div className="flex items-center gap-0.5">
      <TwilioCallButton
        phoneNumber={phone}
        contactName={ticket.odooPartnerDisplayName}
        size={14}
        onCallCompleted={(ph, dur) => ctx.onLogPhoneCall(ticket, ph, dur)}
      />
      <DevWhatsAppButton
        phoneNumber={phone}
        contactId={ticket.contact?.id}
        companyId={ticket.company?.id}
        contactName={ticket.odooPartnerDisplayName}
        size={14}
      />
    </div>
  );

  return [
    // 1. Fase — clickable phase picker matching kanban column colors
    {
      id: 'phase',
      rowClassName,
      content: (
        <PhasePickerPopover
          currentPhase={ticket.phase}
          onChange={(newPhase) => ctx.onPhaseChange(ticket, newPhase)}
        />
      ),
    },
    // 2. Beoordeling — assessment popover
    {
      id: 'beoordeling',
      rowClassName,
      content: (
        <BeoordelingPopover
          value={ticket.beoordeling}
          reden={ticket.beoordelingReden}
          onChange={(v, reden) => ctx.onBeoordelingChange(ticket, v, reden)}
        />
      ),
    },
    // 3. Mails — sent + received emails on the dossier (helpdesk-style indicator).
    // Click opens the dossier sheet which has the email section.
    {
      id: 'emails',
      align: 'center' as const,
      content: (
        <Tooltip
          content={
            emailCount > 0
              ? `${emailCount} ${emailCount === 1 ? 'mail' : 'mails'} op dit dossier${
                  emailUnreadCount > 0 ? ` — ${emailUnreadCount} ongelezen` : ''
                }`
              : 'Nog geen mails op dit dossier'
          }
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              ctx.onOpenDetailSheet(ticket);
            }}
            className={`relative flex items-center gap-1 rounded-md px-1.5 py-1 text-[11px] font-medium shadow-sm transition-all duration-200 ease-in-out hover:scale-105 hover:shadow-md focus:ring-2 active:scale-95 ${
              emailUnreadCount > 0
                ? 'bg-red-100 text-red-600 ring-1 ring-red-200 hover:bg-red-600 hover:text-white focus:ring-red-300'
                : emailCount > 0
                  ? 'bg-blue-100 text-blue-600 hover:bg-blue-600 hover:text-white focus:ring-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-600 hover:text-white focus:ring-gray-300'
            }`}
          >
            <Envelope className="h-3 w-3" />
            <span className="text-[11px] font-semibold">{emailCount}</span>
            {emailUnreadCount > 0 && (
              <div className="absolute -top-1 -right-1">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-red-600"></span>
                </span>
              </div>
            )}
          </button>
        </Tooltip>
      ),
    },
    // 4. Bedrag — red, clickable for invoice details
    {
      id: 'amount',
      content: (
        <Tooltip content="Klik voor dossierdetails">
          <button
            onClick={(e) => { e.stopPropagation(); ctx.onOpenDetailSheet(ticket); }}
            className="font-semibold text-status-bad whitespace-nowrap text-sm hover:underline cursor-pointer"
          >
            {applyMask(totalDebt, 'valuta')}
          </button>
        </Tooltip>
      ),
    },
    // 3. Vennootschap
    {
      id: 'companyName',
      content: (
        <span
          className="text-sm text-neutral-secondary-mid truncate max-w-[120px]"
          title={ticket.cachedCompanyName || ''}
        >
          {ticket.cachedCompanyName || '-'}
        </span>
      ),
    },
    // 4. Unit
    {
      id: 'unit',
      content: (
        <span
          className="text-sm text-neutral-secondary-mid truncate max-w-[80px]"
          title={ticket.cachedUnitLabel || ''}
        >
          {ticket.cachedUnitLabel || '-'}
        </span>
      ),
    },
    // 5. Klant — contact/company with icons, links, phone, WhatsApp
    {
      id: 'customer',
      content: ticket.contact ? (
        <div className="flex items-center gap-1">
          <Tooltip
            content={
              <div className="p-1 text-xs">
                <p>ID: {ticket.contact.id}</p>
                <p>Odoo: {ticket.odooPartnerId}</p>
              </div>
            }
          >
            <Link
              to={`/contact/${ticket.contact.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-moss-primary hover:underline whitespace-nowrap"
              onClick={(e) => e.stopPropagation()}
            >
              <UsersThree weight="duotone" size={16} className="flex-shrink-0" />
              <span className="truncate max-w-[100px]">{ticket.odooPartnerDisplayName}</span>
              <ArrowUpRight className="h-3 w-3 flex-shrink-0" />
            </Link>
          </Tooltip>
          {phoneNumber && renderPhoneActions(phoneNumber)}
        </div>
      ) : ticket.company ? (
        <div className="flex items-center gap-1">
          <Tooltip
            content={
              <div className="p-1 text-xs">
                <p>ID: {ticket.company.id}</p>
                <p>Odoo: {ticket.odooPartnerId}</p>
              </div>
            }
          >
            <Link
              to={`/company/${ticket.company.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-moss-primary hover:underline whitespace-nowrap"
              onClick={(e) => e.stopPropagation()}
            >
              <Buildings weight="duotone" size={16} className="flex-shrink-0" />
              <span className="truncate max-w-[100px]">{ticket.odooPartnerDisplayName}</span>
              <ArrowUpRight className="h-3 w-3 flex-shrink-0" />
            </Link>
          </Tooltip>
          {phoneNumber && renderPhoneActions(phoneNumber)}
        </div>
      ) : (
        <span className="text-sm text-neutral-secondary-mid truncate max-w-[120px]">
          {ticket.odooPartnerDisplayName}
        </span>
      ),
    },
    // 6. Notities — contact/company notes with edit pencil
    {
      id: 'notes',
      content: (
        <div className="flex items-center gap-1">
          {notes ? (
            <Tooltip content={notes}>
              <span className="text-xs text-neutral-secondary-mid truncate max-w-[140px] cursor-help">
                {notes}
              </span>
            </Tooltip>
          ) : (
            <span className="text-xs text-neutral-secondary-mid italic">-</span>
          )}
          {(ticket.contact || ticket.company) && (
            <Tooltip content={notes ? 'Bewerken' : 'Toevoegen'}>
              <button
                onClick={(e) => { e.stopPropagation(); ctx.onEditNotes(ticket); }}
                className="p-0.5 rounded hover:bg-moss-primary/10 text-neutral-secondary-mid hover:text-moss-primary flex-shrink-0"
              >
                <PencilSimple size={12} />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
    // 7. Facturen — invoice count, clickable for details
    {
      id: 'invoices',
      content: (
        <Tooltip content="Klik voor dossierdetails">
          <button
            onClick={(e) => { e.stopPropagation(); ctx.onOpenDetailSheet(ticket); }}
            className="flex items-center gap-1 text-xs text-neutral-secondary-mid hover:text-moss-primary cursor-pointer"
          >
            <Invoice size={14} className="flex-shrink-0" />
            <span>{ticket.cachedInvoiceCount || 0} facturen</span>
          </button>
        </Tooltip>
      ),
    },
    // 8. Te laat — days overdue color-coded
    {
      id: 'daysOverdue',
      content: (
        <span className="text-sm font-medium text-status-bad whitespace-nowrap">
          {maxDays}d
        </span>
      ),
    },
    // 9. Snooze — indicator when snoozed, button when not
    {
      id: 'snooze',
      content: isSnoozed && ticket.snoozeUntil ? (
        <button
          onClick={(e) => { e.stopPropagation(); ctx.onSnooze(ticket); }}
          className="cursor-pointer"
        >
          <SnoozeIndicator snoozeUntil={ticket.snoozeUntil} snoozeReason={ticket.snoozeReason} />
        </button>
      ) : (
        <Tooltip content="Snooze instellen">
          <button
            onClick={(e) => { e.stopPropagation(); ctx.onSnooze(ticket); }}
            className="p-1 rounded hover:bg-neutral-100 text-neutral-secondary-mid hover:text-neutral-700 transition-colors"
          >
            <BellSimple weight="duotone" size={16} />
          </button>
        </Tooltip>
      ),
    },
    // 10. Acties — Brief, incasso actions, payment plans, legal procedure pills, legal count
    {
      id: 'actions',
      align: 'right' as const,
      content: (
        <div className="flex items-center justify-end gap-2">
          {/* Payment reminder button / link */}
          {ctx.generatedLetterUrls[ticketId] ? (
            <Tooltip content="Open betalingsherinnering">
              <a
                href={ctx.generatedLetterUrls[ticketId]}
                target="_blank"
                rel="noopener noreferrer"
                className="text-moss-primary hover:text-moss-primary/80"
                onClick={(e) => e.stopPropagation()}
              >
                <ArrowSquareOut size={18} />
              </a>
            </Tooltip>
          ) : (
            <ButtonNew
              variant="secondaryMoss"
              icon={ctx.generatingLetters[ticketId] ? <Spinner size="sm" /> : <Envelope size={14} />}
              label={ctx.generatingLetters[ticketId] ? '...' : 'Brief'}
              onClick={(e: React.MouseEvent) => { e.stopPropagation(); ctx.onGenerateLetter(ticket); }}
              isDisabled={ctx.generatingLetters[ticketId]}
            />
          )}

          {/* Incasso actions indicator */}
          <Tooltip
            content={
              actionCount > 0
                ? `${actionCount} incasso ${actionCount === 1 ? 'actie' : 'acties'} - Klik om te bekijken`
                : 'Nog geen incasso acties - Klik om toe te voegen'
            }
          >
            <button
              onClick={(e) => { e.stopPropagation(); ctx.onViewIncassoActions(ticket); }}
              className="flex items-center gap-0.5 text-xs font-medium text-moss-primary cursor-pointer hover:text-moss-primary/80"
            >
              <ClipboardText weight="duotone" size={16} />
              <span>{actionCount}</span>
            </button>
          </Tooltip>

          {/* Payment plan indicator */}
          <Tooltip
            content={
              paymentPlanCount > 0
                ? `${paymentPlanCount} afbetalings${paymentPlanCount === 1 ? 'plan' : 'plannen'}${activePlans.length > 0 ? ` (${activePlans.length} actief)` : ''} - Klik om te bekijken`
                : 'Nog geen afbetalingsplannen - Klik om toe te voegen'
            }
          >
            <button
              onClick={(e) => { e.stopPropagation(); ctx.onViewPaymentPlans(ticket); }}
              className={`flex items-center gap-0.5 text-xs font-medium cursor-pointer ${
                activePlans.length > 0
                  ? 'text-blue-700 bg-blue-50 rounded-full px-1.5 py-0.5 hover:bg-blue-100'
                  : 'text-neutral-secondary-mid hover:text-moss-primary'
              }`}
            >
              <ListChecks weight="duotone" size={16} />
              <span>{paymentPlanCount}</span>
            </button>
          </Tooltip>

          {/* Legal procedures - case number pills for active procedures */}
          {activeLegalProcs.length > 0 && activeLegalProcs.some((p) => p.caseNumber) && (
            <div className="flex items-center gap-1">
              {activeLegalProcs
                .filter((p) => p.caseNumber)
                .slice(0, 2)
                .map((proc) => {
                  const nextEventLabel = proc.nextEventType
                    ? PROCEDURE_DATE_TYPE_LABELS[proc.nextEventType]
                    : null;
                  const nextEventDate = proc.nextEventDate
                    ? new Date(proc.nextEventDate).toLocaleDateString('nl-BE', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                      })
                    : null;
                  const pillLabel =
                    nextEventLabel && nextEventDate
                      ? `${proc.caseNumber} - ${nextEventLabel} (${nextEventDate})`
                      : proc.caseNumber;
                  const tooltipContent =
                    nextEventLabel && nextEventDate
                      ? `${LEGAL_PROCEDURE_TYPE_LABELS[proc.type]} - ${proc.caseNumber}\nVolgende: ${nextEventLabel} op ${nextEventDate}`
                      : `${LEGAL_PROCEDURE_TYPE_LABELS[proc.type]} - ${proc.caseNumber}`;

                  return (
                    <Tooltip key={proc.id} content={tooltipContent}>
                      <button
                        onClick={(e) => { e.stopPropagation(); ctx.onViewLegalProcedures(ticket); }}
                        className="text-[11px] font-medium text-status-bad bg-red-50 rounded px-1.5 py-0.5 hover:bg-red-100 cursor-pointer truncate max-w-[200px]"
                      >
                        {pillLabel}
                      </button>
                    </Tooltip>
                  );
                })}
            </div>
          )}

          {/* Legal procedures count indicator */}
          <Tooltip
            content={
              legalProcedureCount > 0
                ? `${legalProcedureCount} juridische ${legalProcedureCount === 1 ? 'procedure' : 'procedures'} - Klik om te bekijken`
                : 'Nog geen juridische procedures - Klik om toe te voegen'
            }
          >
            <button
              onClick={(e) => { e.stopPropagation(); ctx.onViewLegalProcedures(ticket); }}
              className={`flex items-center gap-0.5 text-xs font-medium cursor-pointer ${
                legalProcedureCount > 0
                  ? 'text-status-bad bg-red-50 rounded-full px-1.5 py-0.5 hover:bg-red-100'
                  : 'text-neutral-secondary-mid hover:text-moss-primary'
              }`}
            >
              <Scales weight="duotone" size={16} />
              <span>{legalProcedureCount}</span>
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];
};
